import { UserInfo } from "../helper/Context";
import { Row, Col, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import ReactPlayer from "react-player";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faComments, 
  faWandMagicSparkles, 
  faMicroscope,
  faLungs, 
  faDna,
  faBone,
  faArrowRight,
  faMagic
} from "@fortawesome/free-solid-svg-icons";
import { canCreatePresentations, canCreateScrollytellings } from "../helper/BusinessLogic";
import { useState, useContext } from "react";
import YouTubeVideoPopup from "../components/YouTubeVideoPopup";
import AccessCodeModal from "../components/AccessCodeModal";
import './DashboardPage.css';

const DashboardBody = (props) => {
    const [activeTab, setActiveTab] = useState('communicate');
    const [explainerPrompt, setExplainerPrompt] = useState('');
    const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
    const [accessCodeAction, setAccessCodeAction] = useState(null);
    const [accessCodeFeatureName, setAccessCodeFeatureName] = useState('');
    const [accessCodePermission, setAccessCodePermission] = useState('');
    const [explainerAccessGranted, setExplainerAccessGranted] = useState(false);
    const [segmentationAccessGranted, setSegmentationAccessGranted] = useState(false);
    const { userInfo } = useContext(UserInfo);
    const navigate = useNavigate();

    const hasExplainerAccess = explainerAccessGranted;

    const handleExplainerTabClick = () => {
        setActiveTab('explainers');
    };

    const handleFeatureClick = (path, state = {}) => {
        // Navigate to the feature page - route-level gates (PremiumFeatureGate/AIFeatureGate) 
        // will handle showing the appropriate popup if the user doesn't have access
            navigate(path, { state });
    };

    const handleGenerateExplainer = () => {
        if (explainerPrompt.trim()) {
            navigate('/tour', { state: { prompt: explainerPrompt } });
        }
    };

    const upgradeCapsuleStyle = {
        color: 'cyan',
        fontSize: '0.8em',
        border: '1px solid cyan',
        borderRadius: '1em',
        padding: '0.2em 0.6em',
        marginLeft: '0.5em'
    };

    return (
      <>
        <h1 className="gradient-text">Dashboard</h1>
        <div style={{ marginTop: '12px', marginBottom: '20px', textAlign: 'center' }}>
          <YouTubeVideoPopup 
            videoUrl="https://youtu.be/OYqa_gJDFfk"
            linkText="Video tour of the dashboard"
            variant="text"
          />
        </div>

        {/* Content Creation Wizard CTA */}
        <div className="wizard-cta-banner">
          <span className="wizard-cta-sub">Not sure where to start?</span>
          <Button
            className="wizard-cta-btn"
            onClick={() => navigate('/dashboard', { state: { openWizard: true } })}
          >
            <FontAwesomeIcon icon={faMagic} className="me-1" />
            Content Wizard
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="dashboard-tabs">
            <button 
                className={`dashboard-tab ${activeTab === 'communicate' ? 'active' : ''}`}
                onClick={() => setActiveTab('communicate')}
            >
                <FontAwesomeIcon icon={faComments} className="tab-icon" />
                <span>Communicate</span>
            </button>
            <button 
                className={`dashboard-tab ${activeTab === 'explainers' ? 'active' : ''}`}
                onClick={handleExplainerTabClick}
            >
                <FontAwesomeIcon icon={faWandMagicSparkles} className="tab-icon" />
                <span>Generate Explainer Videos</span>
                <span className="tab-badge tab-badge-alpha">Alpha</span>
            </button>
            <button 
                className={`dashboard-tab ${activeTab === 'agentic' ? 'active' : ''}`}
                onClick={() => setActiveTab('agentic')}
            >
                <FontAwesomeIcon icon={faMicroscope} className="tab-icon" />
                <span>Live Visual Science</span>
                <span className="tab-badge tab-badge-beta">Beta</span>
            </button>
        </div>

        {/* Tab Content */}
        <div className="dashboard-tab-content">
            {/* Communicate Tab */}
            {activeTab === 'communicate' && (
                <div className="tab-panel fade-in">
                    <h3 className="tab-panel-title">Create Professional Scientific Communications</h3>
                    <p className="tab-panel-subtitle">
                        Choose from our suite of tools to create engaging presentations and visual content
                    </p>
                    <Row className="g-4 mt-2">
                        <Col md={6} lg={3}>
                            <div 
                                className="feature-card feature-card-with-video"
                                onClick={() => handleFeatureClick('/create-presentation', { new: true })}
                            >
                                <div className="feature-card-video">
                                    <ReactPlayer
                                        url="https://vimeo.com/1135004447"
                                        playing={true}
                                        loop={true}
                                        muted={true}
                                        playsInline={true}
                                        width="100%"
                                        height="100%"
                                        controls={false}
                                        config={{
                                            vimeo: {
                                                playerOptions: {
                                                    autoplay: true,
                                                    muted: true,
                                                    playsinline: true,
                                                    background: true
                                                }
                                            }
                                        }}
                                    />
                                </div>
                                <div className="feature-card-content">
                                    <h5 className="feature-card-title">Slide Deck</h5>
                                    <p className="feature-card-description">
                                        Create stunning presentation slides with interactive visuals
                                    </p>
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '8px' }}>
                                        <YouTubeVideoPopup 
                                            videoUrl="https://youtu.be/OjvDDX9fBgg"
                                            linkText="Watch tutorial"
                                            variant="text"
                                        />
                                    </div>
                                    {!canCreatePresentations(userInfo) && (
                                        <span style={upgradeCapsuleStyle}>Upgrade</span>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col md={6} lg={3}>
                            <div 
                                className="feature-card feature-card-with-video"
                                onClick={() => handleFeatureClick('/scrollytelling/builder', { new: true })}
                            >
                                <div className="feature-card-video">
                                    <ReactPlayer
                                        url="https://vimeo.com/1091699735"
                                        playing={true}
                                        loop={true}
                                        muted={true}
                                        playsInline={true}
                                        width="100%"
                                        height="100%"
                                        controls={false}
                                        config={{
                                            vimeo: {
                                                playerOptions: {
                                                    autoplay: true,
                                                    muted: true,
                                                    playsinline: true,
                                                    background: true
                                                }
                                            }
                                        }}
                                    />
                                </div>
                                <div className="feature-card-content">
                                    <h5 className="feature-card-title">Scrollytelling</h5>
                                    <p className="feature-card-description">
                                        Tell your story with immersive scroll-based narratives
                                    </p>
                                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '8px' }}>
                                        <YouTubeVideoPopup 
                                            videoUrl="https://youtu.be/-xsfRuVLfLA"
                                            linkText="Watch tutorial"
                                            variant="text"
                                        />
                                    </div>
                                    {!canCreateScrollytellings(userInfo) && (
                                        <span style={upgradeCapsuleStyle}>Upgrade</span>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col md={6} lg={3}>
                            <div 
                                className="feature-card feature-card-with-video"
                                onClick={() => navigate('/dashboard/upload')}
                            >
                                <div className="feature-card-video">
                                    <ReactPlayer
                                        url="https://vimeo.com/1135007069"
                                        playing={true}
                                        loop={true}
                                        muted={true}
                                        playsInline={true}
                                        width="100%"
                                        height="100%"
                                        controls={false}
                                        config={{
                                            vimeo: {
                                                playerOptions: {
                                                    autoplay: true,
                                                    muted: true,
                                                    playsinline: true,
                                                    background: true
                                                }
                                            }
                                        }}
                                    />
                                </div>
                                <div className="feature-card-content">
                                    <h5 className="feature-card-title">Interactive Visual</h5>
                                    <p className="feature-card-description">
                                        Upload and create standalone interactive visualizations
                                    </p>
                                </div>
                            </div>
                        </Col>
                            <Col md={6} lg={3}>
                                <div 
                                    className="feature-card feature-card-with-logo"
                                    onClick={() => navigate('/doi')}
                                >
                                    <div className="feature-card-logo-container">
                                        <img 
                                            src="https://assets.crossref.org/logo/crossref-logo-200.svg" 
                                            alt="Crossref Logo" 
                                            className="feature-card-logo"
                                        />
                                    </div>
                                    <div className="feature-card-content">
                                        <h5 className="feature-card-title">DOI Request</h5>
                                        <p className="feature-card-description">
                                            Request a Digital Object Identifier (DOI) for your work
                                        </p>
                                    {userInfo?.tier === 'free' && (
                                        <span style={upgradeCapsuleStyle}>Upgrade</span>
                                    )}
                                    </div>
                                </div>
                            </Col>
                    </Row>
                </div>
            )}

            {/* Generate Explainers Tab */}
            {activeTab === 'explainers' && (
                <div className="tab-panel fade-in">
                    <h3 className="tab-panel-title">Generate Explainer Videos</h3>
                    <p className="tab-panel-subtitle">
                        Create AI-powered explainer videos and clips with audio narration.
                    </p>
                    <div className="explainer-prompt-container" style={{position: 'relative'}}>
                        <Form.Group className="mb-4">
                            <Form.Label className="explainer-label">
                                Description of Video
                                <span className="explainer-label-subtitle">
                                    We currently support medical and biomedical topics
                                </span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                className="explainer-textarea"
                                value={explainerPrompt}
                                placeholder="e.g., 'Show how the digestive system works', 'How do beta blockers affect the heart?', or 'Explain the overall structure of the brain'"
                                onChange={(e) => setExplainerPrompt(e.target.value)}
                            />  
                        </Form.Group>
                        <Button
                            className="generate-explainer-btn"
                            onClick={() => {
                                if (!hasExplainerAccess) {
                                    setAccessCodeFeatureName('Generate Explainer Videos');
                                    setAccessCodePermission('create_explainers');
                                    setAccessCodeAction(() => () => {
                                        setExplainerAccessGranted(true);
                                        handleGenerateExplainer();
                                    });
                                    setShowAccessCodeModal(true);
                                } else {
                                    handleGenerateExplainer();
                                }
                            }}
                            disabled={!explainerPrompt.trim()}
                        >
                            Generate Video
                            <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Live Agentic Science Tab */}
            {activeTab === 'agentic' && (
                <div className="tab-panel fade-in">
                    <h3 className="tab-panel-title">Advanced Scientific Tools</h3>
                    <p className="tab-panel-subtitle">
                        Explore AI-enabled visualization and analysis tools for live insight. 
                    </p>
                    <Row className="g-4 mt-2">
                        <Col md={6} lg={4}>
                            <div 
                                className="feature-card"
                                onClick={() => {
                                    if (!segmentationAccessGranted) {
                                        setAccessCodeFeatureName('Clinical Imaging Segmentation');
                                        setAccessCodePermission('organ_segmentation');
                                        setAccessCodeAction(() => () => {
                                            setSegmentationAccessGranted(true);
                                            navigate('/organ-segmentation');
                                        });
                                        setShowAccessCodeModal(true);
                                    } else {
                                        navigate('/organ-segmentation');
                                    }
                                }}
                            >
                                <div className="feature-card-icon agentic-icon">
                                    <FontAwesomeIcon icon={faLungs} />
                                </div>
                                <h5 className="feature-card-title">Clinical Imaging Segmentation</h5>
                                <p className="feature-card-description">
                                    Identify and segment organs in 3D from unlabeled clinical imaging stacks (deep-learning powered). 
                                </p>
                            </div>
                        </Col>
                        <Col md={6} lg={4}>
                            <div 
                                className="feature-card"
                                onClick={() => navigate('/molecular-viewer')}
                            >
                                <div className="feature-card-icon agentic-icon">
                                    <FontAwesomeIcon icon={faDna} />
                                </div>
                                <h5 className="feature-card-title">Live Molecular Viewer</h5>
                                <p className="feature-card-description">
                                    Natural language faciliated chemistry viewer. (AI-powered reaction prediction coming soon)
                                </p>
                                <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '8px' }}>
                                    <YouTubeVideoPopup 
                                        videoUrl="https://youtu.be/hfY2y3XGLmI"
                                        linkText="Watch tutorial"
                                        variant="text"
                                    />
                                </div>
                                {userInfo?.tier === 'free' && (
                                    <span style={upgradeCapsuleStyle}>Upgrade</span>
                                )}
                            </div>
                        </Col>
                        <Col md={6} lg={4}>
                            <div 
                                className="feature-card feature-card-disabled"
                                title="Coming soon - Contact us for early access"
                            >
                                <div className="ribbon-wrapper-small">
                                    <div className="ribbon-small">Coming Soon</div>
                                </div>
                                <div className="feature-card-icon agentic-icon">
                                    <FontAwesomeIcon icon={faBone} />
                                </div>
                                <h5 className="feature-card-title">Musculoskeletal Kinematics</h5>
                                <p className="feature-card-description">
                                    Simulate and analyze musculoskeletal movement and mechanics
                                </p>
                            </div>
                        </Col>
                    </Row>
                </div>
            )}
        </div>

        {ReactDOM.createPortal(
          <AccessCodeModal
            show={showAccessCodeModal}
            onHide={() => setShowAccessCodeModal(false)}
            featureName={accessCodeFeatureName}
            featureKey={accessCodePermission}
            onAccessGranted={() => {
              setShowAccessCodeModal(false);
              if (accessCodeAction) accessCodeAction();
            }}
          />,
          document.body
        )}
      </>
    );
  };
  
  export default DashboardBody; 